import type { CollectionViewParams, CollectionViewParamsConfig } from './types';
import { getDefaultCollectionViewParams } from './defaults';
import { restoreCollectionFilters, restoreCollectionSort } from './validation';
import { isPlainObject } from 'lodash';

const CACHE_VERSION = 2;

const getCacheKey = (pathname: string, namespace?: string) =>
  `collection-view:${pathname}${namespace ? `:${namespace}` : ''}`;

const removeCacheEntry = (pathname: string, namespace?: string) => {
  try {
    localStorage.removeItem(getCacheKey(pathname, namespace));
  } catch {
    // Storage may be unavailable.
  }
};

export const readCollectionViewParamsCache = <Filters>(
  pathname: string,
  config: CollectionViewParamsConfig<Filters>,
): CollectionViewParams<Filters> | null => {
  if (!config.cache) return null;

  const cacheKey = getCacheKey(pathname, config.cacheKey);
  try {
    const cachedValue = localStorage.getItem(cacheKey);
    if (!cachedValue) return null;

    const parsed = JSON.parse(cachedValue);
    if (!isPlainObject(parsed) || parsed.version !== CACHE_VERSION || !isPlainObject(parsed.params)) {
      removeCacheEntry(pathname, config.cacheKey);
      return null;
    }

    const cachedFilters = parsed.params.filters;
    const hasCachedFilters = isPlainObject(cachedFilters) && Object.keys(cachedFilters).length > 0;
    const defaults = getDefaultCollectionViewParams(config);
    const cachedSort = parsed.params.sort;

    return {
      filters: hasCachedFilters ? restoreCollectionFilters(cachedFilters, config) : defaults.filters,
      sort: restoreCollectionSort(cachedSort, config),
    };
  } catch {
    removeCacheEntry(pathname, config.cacheKey);
    return null;
  }
};

export const writeCollectionViewParamsCache = <Filters>(
  pathname: string,
  params: CollectionViewParams<Filters>,
  config: CollectionViewParamsConfig<Filters>,
) => {
  if (!config.cache) return;

  try {
    const cachedValue = getCacheKey(pathname, config.cacheKey);
    localStorage.setItem(cachedValue, JSON.stringify({ version: CACHE_VERSION, params }));
  } catch {
    // The URL remains the source of truth when storage is unavailable.
  }
};

export const clearCollectionViewParamsCache = (pathname: string, namespace?: string) => {
  removeCacheEntry(pathname, namespace);
};
